import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FlaskConical, Radio, RefreshCw, ShieldCheck, Trash2 } from "lucide-react";

const MODES = [
  {
    value: "test",
    label: "Test",
    icon: FlaskConical,
    blurb: "Nothing is sent. Every WhatsApp, SMS and email is written to the log below instead.",
  },
  {
    value: "live_test",
    label: "Live test",
    icon: ShieldCheck,
    blurb: "Real messages, but only to the clinic's own number. Everything else is logged, not sent.",
  },
  {
    value: "live",
    label: "Live",
    icon: Radio,
    blurb: "Real messages to real customers. Twilio and Resend are charged for every send.",
  },
];

const OUTCOME_STYLE = {
  mocked: "border-sky-500/40 text-sky-700 bg-sky-500/10",
  sent: "border-primary/40 text-primary bg-primary/10",
  failed: "border-destructive/40 text-destructive bg-destructive/10",
  capped: "border-amber-500/50 text-amber-700 bg-amber-500/10",
};

const when = (iso) => {
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

/**
 * Admin → Settings: the Test / Live switch for everything the app sends, plus the
 * ledger of what went out (or would have). The mode is stored in the database, so
 * the clinic can flip it here at any time without a redeploy.
 */
export const NotificationModeCard = () => {
  const [status, setStatus] = useState(null);
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState("");
  const [busy, setBusy] = useState(false);

  const loadStatus = () => api.get("/admin/notifications/mode").then(({ data }) => setStatus(data)).catch(() => {});
  const loadLog = () =>
    api.get("/admin/notifications/log", { params: { limit: 50 } })
      .then(({ data }) => setEntries(data.entries || []))
      .catch(() => {});

  useEffect(() => { loadStatus(); loadLog(); }, []);

  const choose = async (value) => {
    if (value === status?.mode) return;
    if (value === "live" && confirming !== "live") {
      setConfirming("live");
      return toast.warning("Tap Live once more to start charging real messages");
    }
    setBusy(true);
    setConfirming("");
    try {
      const { data } = await api.put("/admin/notifications/mode", { mode: value });
      setStatus(data);
      toast.success(`Notifications are now in ${MODES.find((m) => m.value === value)?.label} mode`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const clearMocked = async () => {
    try {
      const { data } = await api.delete("/admin/notifications/log");
      toast.success(`${data.deleted} test message(s) cleared`);
      loadLog();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!status) return null;
  const current = MODES.find((m) => m.value === status.mode) || MODES[0];

  return (
    <Card className="border-border" data-testid="notify-mode-card">
      <CardContent className="p-6 lg:p-8">
        <div className="flex items-start justify-between gap-4 mb-1">
          <h3 className="font-display text-2xl flex items-center gap-2">
            <current.icon size={18} /> Message sending
          </h3>
          <Badge
            variant="outline"
            className={status.mode === "live"
              ? "border-primary/40 text-primary bg-primary/10"
              : "border-sky-500/40 text-sky-700 bg-sky-500/10"}
            data-testid="notify-mode-badge"
          >
            {current.label} mode
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground mb-5">{current.blurb}</p>

        <div className="grid sm:grid-cols-3 gap-3 mb-6">
          {MODES.map((m) => (
            <button
              key={m.value}
              onClick={() => choose(m.value)}
              disabled={busy}
              data-testid={`notify-mode-${m.value}`}
              className={`text-left rounded-xl border p-4 transition-all hover:-translate-y-0.5 ${
                status.mode === m.value
                  ? "border-primary bg-primary/5 shadow-[0_10px_24px_-18px_rgba(33,83,52,0.6)]"
                  : "border-border hover:border-primary/40"
              }`}
            >
              <div className="flex items-center gap-2 text-sm font-medium">
                <m.icon size={14} className={status.mode === m.value ? "text-primary" : "text-muted-foreground"} />
                {m.label}
                {confirming === m.value && <span className="text-xs text-amber-700">tap again</span>}
              </div>
              <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{m.blurb}</p>
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mb-5">
          <Badge variant="outline" data-testid="notify-sent-today">{status.sent_today} really sent today</Badge>
          <Badge variant="outline">Live-test cap {status.daily_cap}/day</Badge>
          <Badge variant="outline" data-testid="notify-whitelist">Live test reaches {status.whitelist.join(", ")}</Badge>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="btn-pill h-9" onClick={() => { setOpen(!open); loadLog(); }}
                  data-testid="notify-log-toggle">
            {open ? "Hide" : "Show"} message log
          </Button>
          <Button variant="ghost" className="btn-pill h-9" onClick={loadLog} data-testid="notify-log-refresh">
            <RefreshCw size={13} className="mr-1.5" /> Refresh
          </Button>
          <Button variant="ghost" className="btn-pill h-9 text-destructive" onClick={clearMocked}
                  data-testid="notify-log-clear">
            <Trash2 size={13} className="mr-1.5" /> Clear test messages
          </Button>
        </div>

        {open && (
          <div className="mt-5 space-y-2" data-testid="notify-log-list">
            {entries.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="notify-log-empty">
                Nothing sent yet. Place a test order or send a test alert and it will appear here.
              </p>
            ) : (
              entries.map((e) => (
                <details key={e.id} className="rounded-xl border border-border p-3" data-testid={`notify-log-${e.id}`}>
                  <summary className="flex flex-wrap items-center gap-2 cursor-pointer text-sm">
                    <Badge variant="outline" className="capitalize">{e.channel}</Badge>
                    <Badge variant="outline" className={OUTCOME_STYLE[e.outcome] || ""}>{e.outcome}</Badge>
                    <span className="truncate max-w-[220px]">{e.to || "—"}</span>
                    <span className="ml-auto text-xs text-muted-foreground">{when(e.created_at)}</span>
                  </summary>
                  <div className="mt-3 text-xs space-y-2">
                    {e.subject && <div><span className="overline">Subject</span><div>{e.subject}</div></div>}
                    <div><span className="overline">Detail</span><div className="text-muted-foreground">{e.detail || "—"}</div></div>
                    <pre className="whitespace-pre-wrap rounded-lg bg-muted/60 p-3 text-[11px] leading-relaxed max-h-64 overflow-auto">
                      {e.body || "—"}
                    </pre>
                  </div>
                </details>
              ))
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NotificationModeCard;
